import React from 'react';

import { DataTableFilter } from '../components/data-table/DataTable/interfaces';
import { CheckFilter, DateFilter } from '../components/data-table/filters';
import { ICheckboxOption } from '../components/forms/check/CheckboxOption/interfaces';
import { camelToSnake, capitalizeStr } from './stringHelpers';

import {
  ColumnFilterData,
  ColumnFilterValue,
  ComparisonValueFilterField,
  FilterFields,
  sortField,
} from '../components/ManagedDataTable/interfaces';
import {
  readObjectFromLocalStorage,
  storeDataInLocalStorage,
} from './localStorageHelper';
import { storedFilterSettings } from './interfaces';
import { DateFilterValue } from '../components/data-table/filters/DateFilter/interfaces';

export function buildCheckFilter(
  field: string,
  label: string,
  list: { id: string | number; name: string }[] | undefined,
  setFilterFieldData: (field: string, val: ColumnFilterValue) => void,
  value?: ColumnFilterValue
): DataTableFilter {
  return {
    active: !!((value || []) as Array<string | number>).length,
    component: (
      <CheckFilter
        label={label}
        value={value as Array<string | number>}
        onChange={(val) => setFilterFieldData(field, val)}
        options={
          list?.map(
            (item): ICheckboxOption => ({
              label: capitalizeStr(item.name),
              value: item.id,
            })
          ) || []
        }
      />
    ),
  };
}

export function buildDateFilter(
  field: string,
  label: string,
  setFilterFieldData: (field: string, val: DateFilterValue[]) => void, // set one or more ComparisonValueFilterFields
  value?: DateFilterValue[] // this is also one or more ComparisonValueFilterField. The component needs to figure out which option is selected.
): DataTableFilter {
  return {
    active: value !== undefined && value.length > 0,
    component: (
      <DateFilter
        label={label}
        onChange={(val) => setFilterFieldData(field, val)}
        value={value}
      />
    ),
  };
}

const isDateFilterValue = (filterItemValue: ColumnFilterValue): boolean => {
  return (
    typeof filterItemValue === 'object' &&
    'operator' in filterItemValue &&
    'value' in filterItemValue
  );
};

const isMultiFilterValue = (filterItemValue: ColumnFilterValue): boolean => {
  return (
    Array.isArray(filterItemValue) &&
    filterItemValue.length > 0 &&
    (typeof filterItemValue[0] === 'number' ||
      typeof filterItemValue[0] === 'string')
  );
};

const filterValueHasOperatorFields = (
  filterItemValue: ColumnFilterValue
): boolean => {
  return (
    Array.isArray(filterItemValue) &&
    filterItemValue.length > 0 &&
    isDateFilterValue(filterItemValue[0])
  );
};

const getOperatorValue = (
  field: string,
  value: DateFilterValue
): ComparisonValueFilterField => {
  return {
    field,
    operator: value.operator,
    value: value.value,
  };
};

export const mapFilterDataToFilterFields = (filterData: ColumnFilterData) => {
  const filters: FilterFields = [];

  Object.keys(filterData).forEach((item) => {
    const filterItemValue = filterData[item];

    if (isDateFilterValue(filterItemValue)) {
      filters.push(
        getOperatorValue(
          camelToSnake(item as string),
          filterItemValue as DateFilterValue
        )
      );
    } else if (isMultiFilterValue(filterItemValue)) {
      filters.push({
        field: camelToSnake(item as string),
        values: filterItemValue as Array<string | number>,
      });
    } else if (filterValueHasOperatorFields(filterItemValue)) {
      for (const value of filterItemValue as DateFilterValue[]) {
        filters.push(getOperatorValue(camelToSnake(item as string), value));
      }
    }
  });

  return filters.length ? filters : undefined;
};

export const isSameSortField = (
  sortFieldA: sortField,
  sortFieldB: sortField
): boolean => {
  return (
    sortFieldA.field === sortFieldB.field && sortFieldA.sort === sortFieldB.sort
  );
};

export const storeFilterAndSortSettingsInLocalStorage = (
  storageKey: string,
  sort?: sortField,
  filterData?: ColumnFilterData
): void => {
  const filterSettings: storedFilterSettings = {};

  if (sort) {
    filterSettings.sort = sort;
  }

  if (filterData) {
    filterSettings.filterData = filterData;
  }

  storeDataInLocalStorage(`${storageKey}-filter-settings`, filterSettings);
};

export const readFilterAndSortSettingsFromLocalStorage = (
  storageKey: string
): null | storedFilterSettings => {
  const data = readObjectFromLocalStorage(`${storageKey}-filter-settings`);

  if (!data) {
    return null;
  }

  return data as storedFilterSettings;
};

export const hasActiveFilters = (filterData: ColumnFilterData): boolean => {
  if (filterData && Object.keys(filterData).length > 0) {
    for (const n in filterData) {
      const filter = filterData[n];

      if (Array.isArray(filter) && filter.length > 0) {
        return true;
      } else if (!Array.isArray(filter) && filter !== undefined) {
        return true;
      }
    }
  }

  return false;
};
